import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $recipientListStore, $recipientRecentStore } from '@lib/store';
import { cn, PaletteUtils } from '@shared/utils';
import { type RecipientResponse, SortType, TransferRecipientTypeEnum } from '@shared/api';
import { SelectRecipient } from './selectRecipient';
import { RecentRecipient } from './recentRecipient';
import { RecipientDetails } from './recipientDetails';
import { ErrorMessage, Radio, Text } from '@shared/widgets';
import { View } from '@widgets/view';

enum RecipientType {
  Existing = 'Existing Recipient',
  New = 'New Recipient',
}
const RECIPIENT_TYPE_OPTIONS = Object.entries(RecipientType).map(([, text]) => ({ text, value: text }));

interface RecipientSelectBlockProps {
  className?: string;
  type: TransferRecipientTypeEnum;
  isInternalAccount?: boolean;
  recipient?: RecipientResponse | null;
  onChangeRecipient: (val?: RecipientResponse | null) => void;
  isShowRecipientDetails: boolean;
  onToggleRecipientDetails: () => void;
  error?: ReactNode;
}

export const RecipientSelectBlock = (props: RecipientSelectBlockProps) => {
  const recipientListStore = useUnit($recipientListStore.store);
  const recipientRecentStore = useUnit($recipientRecentStore.store);

  const [recipientType, setRecipientType] = useState<RecipientType>(RecipientType.Existing);
  const [searchValue, setSearchValue] = useState<string>('');
  const { setValue } = useFormContext();
  const { t } = useTranslation('payments');

  useEffect(() => {
    $recipientRecentStore.events.getRecent({ Type: props.type, isInternalAccount: !!props.isInternalAccount });
    return () => $recipientRecentStore.events.clear();
  }, [props.isInternalAccount, props.type]);

  useEffect(() => {
    $recipientListStore.events.getRecipients({
      Count: 100,
      ReturnTotalCount: true,
      SearchQuery: searchValue,
      Type: props.type,
      isInternalAccount: !!props.isInternalAccount,
      SortType: SortType.Asc,
    });
    return () => $recipientListStore.events.clear();
  }, [searchValue, props.isInternalAccount, props.type]);

  const recipients = useMemo(() => {
    return recipientListStore.data.data.filter((recipient) => recipient.type === props.type);
  }, [props.type, recipientListStore.data.data]);
  const recentRecipients = useMemo(() => recipientRecentStore.data.filter((recipient) => recipient.type === props.type), [recipientRecentStore.data]);
  return (
    <div className={cn('flex flex-col gap-2', props.className)}>
      <Radio
        orientation='horizontal'
        options={RECIPIENT_TYPE_OPTIONS}
        value={recipientType}
        onChange={(value) => {
          const type = value as RecipientType;
          setRecipientType(type);
          switch (type) {
            case RecipientType.Existing:
              props.onChangeRecipient(undefined);
              setValue('currency', undefined);
              break;
            case RecipientType.New:
              props.onChangeRecipient(null);
              break;
          }
        }}
      />
      {recipientType === RecipientType.Existing && (
        <>
          <div>
            <SelectRecipient
              label={t('recipient.placeholders.transferTo')}
              items={recipients}
              value={props.recipient}
              onChange={props.onChangeRecipient}
              searchValue={searchValue}
              onSearch={setSearchValue}
            />
            <ErrorMessage text={props.error} />
          </div>
          {!props.recipient && (
            <View
              loading={recipientRecentStore.loading}
              error={recipientRecentStore.error}
              onRefresh={() => $recipientRecentStore.events.getRecent({ Type: props.type, isInternalAccount: !!props.isInternalAccount })}
            >
              {recentRecipients.length > 0 && (
                <div className='flex flex-col'>
                  <Text className={cn('my-3', PaletteUtils.isVcfcPalette && 'dark:text-white/50')}>{t('recipient.title')}</Text>
                  <div className='flex flex-row overflow-x-auto'>
                    {recentRecipients.map((recipient, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          'mr-2 flex cursor-pointer rounded-sm px-2 py-3',
                          PaletteUtils.isInsigniaPalette && ['bg-ice dark:bg-eerie-black', 'hover:bg-ghost-white dark:hover:bg-transport-black'],
                          PaletteUtils.isVcfcPalette && ['bg-ghost-white dark:bg-light/5', 'hover:bg-gainsborough dark:hover:bg-light/20'],
                        )}
                        onClick={() => props.onChangeRecipient(recipient)}
                      >
                        <RecentRecipient recipient={recipient} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </View>
          )}
          {props.recipient && (
            <RecipientDetails
              recipient={props.recipient}
              onToggle={props.onToggleRecipientDetails}
              isShowRecipientDetails={props.isShowRecipientDetails}
            />
          )}
        </>
      )}
    </div>
  );
};
